import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import { CreateMemoRequest, DriverSchedule, DriverScheduleMonthRequest, Memo, MemoMonthListRequest } from "@/interface/additional";
import { toast } from "@/lib/toast";

const memoQueryKey = "memoMonthList";
const driverScheduleQueryKey = "driverScheduleShared";

// 월별 메모 목록 조회
export const useMemoMonthList = (params: MemoMonthListRequest) => {
  return useQuery<Memo[]>({
    queryKey: [memoQueryKey, params],
    queryFn: () => {
      const queryParams = new URLSearchParams({
        year: params.year.toString(),
        month: params.month.toString(),
        category: params.category ?? "",
        keyword: params.keyword ?? "",
      });

      return api.get(`/memo?${queryParams.toString()}`);
    },
  });
};

// 메모 생성/수정
export const useCreateMemo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateMemoRequest) => {
      // body에 id가 있으면 수정, 없으면 생성
      const response = await api.post("/memo", data);
      return response;
    },
    onSuccess: (_, variables) => {
      const message = variables.id ? "메모가 수정되었습니다." : "메모가 생성되었습니다.";
      toast.success(message);
      queryClient.invalidateQueries({ queryKey: [memoQueryKey] });
      queryClient.invalidateQueries({ queryKey: [driverScheduleQueryKey] });
    },
    onError: (err, variables) => {
      console.error(err);
      const message = variables.id ? "메모 수정 실패" : "메모 생성 실패";
      toast.error(message);
    },
  });
};

export const useDeleteMemo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.delete(`/memo/${id}`);
      return response;
    },
    onSuccess: res => {
      console.log(res);
      if (res?.code === 200) {
        toast.success("메모가 삭제되었습니다.");
        queryClient.invalidateQueries({ queryKey: [memoQueryKey] });
        queryClient.invalidateQueries({ queryKey: [driverScheduleQueryKey] });
      } else {
        toast.error("메모 삭제 실패");
      }
    },
    onError: err => {
      console.error(err);
      toast.error("메모 삭제 실패");
    },
  });
};

// 공유된 기사 일정 월별 조회 (is_shared = true)
export const useDriverScheduleShared = (params: DriverScheduleMonthRequest) => {
  return useQuery<DriverSchedule[]>({
    queryKey: [driverScheduleQueryKey, params],
    queryFn: () => {
      const queryParams = new URLSearchParams({
        year: params.year.toString(),
        month: params.month.toString(),
      });

      return api.get(`/driver-schedule/shared?${queryParams.toString()}`);
    },
  });
};
